/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import GenericButton from "@ui-components/Button/GenericButton";
import {
  buildPredicate,
  FilterField,
  labelId,
  ParsedFilter as OldParsed,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { ReactComponent as IconDownload } from "../../../../shared/assets/Icons/download-solid.svg";
import { downloadReports, getReports } from "./request/request";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useAppSelector } from "@redux/hooks";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import { useRequireTenantType } from "@shared/hooks/useRequireTenantType";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";

export interface Report {
  id: number;
  tenantId: number;
  customer_id: string;
  customer_name: string;
  date: string;
  reportMonth: number;
  reportYear: number;
  /** Report scope discriminator: Odoo orderId or "default" (main report). */
  reportGroup?: string;
  /** Human-readable label for the group (e.g. "OC-82"); null for the main report. */
  reportGroupLabel?: string | null;
  status: string;
  generatedAt: string;
  createdAt: string;
}

/** Label shown in the Purchase Order column. The "default" group is the main
 * report (legacy/no-order services); other groups carry their PO label. */
function formatReportGroup(report: Report): string {
  if (!report.reportGroup || report.reportGroup === "default") {
    return "Main";
  }
  return report.reportGroupLabel || `OC-${report.reportGroup}`;
}

/** Format date as "February, 2026" for display and filtering (year + month name match). */
function formatDateMonthYear(dateStr: string): string {
  if (!dateStr) return "";
  const datePart = dateStr.split("T")[0];
  const [year, month] = datePart.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  const monthName = date.toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${monthName}, ${year}`;
}

/**
 * Define table columns for Reports
 */
const baseColumns: GenericTableColumn<Report>[] = [
  {
    header: "ID",
    accessor: "id",
  },
  {
    header: "Customer Name",
    accessor: "customer_name",
  },
  {
    header: "Customer ID",
    accessor: "customer_id",
  },
  {
    header: "Date",
    accessor: "date",
    renderCell: (row) => {
      return formatDateMonthYear(row.date);
    },
  },
  {
    header: "Purchase Order",
    accessor: "reportGroupLabel",
    renderCell: (row) => (
      <GenericTag variant="neutral" label={formatReportGroup(row)} size="small" />
    ),
  },
  {
    header: "Status",
    accessor: "status",
    renderCell: (row) => {
      const status = row.status || "";
      let variant: "success" | "warning" | "error" | "neutral" = "neutral";
      let label = status;

      if (status === "completed") {
        variant = "success";
        label = "Completed";
      } else if (status === "pending") {
        variant = "warning";
        label = "Pending";
      } else if (status === "failed") {
        variant = "error";
        label = "Failed";
      } else {
        label = status.charAt(0).toUpperCase() + status.slice(1);
      }

      return <GenericTag variant={variant} label={label} size="small" />;
    },
  },
];

export default function ReportsPage() {
  // ========== Access Control ==========
  // Check access status to prevent unnecessary API calls
  const { isAllowed } = useRequireTenantType(["PARTNER", "GLOBAL_ADMIN"]);

  // ========= 1) Table data =========
  const [reports, setReports] = useState<Report[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [listFetchError, setListFetchError] = useState<string | null>(null);
  const overlaySpinner = useProcessingOverlay(isFetching);

  // ========== Download state ==========
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(
    null
  );

  // ========== Error Alert state ==========
  const [errorAlert, setErrorAlert] = useState<{
    isOpen: boolean;
    message: string;
  }>({
    isOpen: false,
    message: "",
  });

  // ========== Auth0 ==========
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  // ========== Redux Selection State ==========
  const { tenantId } = useAppSelector((state: any) => state.selection);

  // ========== Filters ==========
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);

  // ------------------------------------------------------------------------------------
  // useEffect: Fetch Reports on mount and when tenantId changes
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    // Early return if access is not confirmed - prevents unnecessary API calls
    if (!isAllowed) return;
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (!tenantId) return;

    setHasFetched(false);

    const fetchReports = async () => {
      try {
        setIsFetching(true);
        setListFetchError(null);

        const token = await getAccessTokenSilently();
        const response = await getReports(token, tenantId);
        const raw =
          (response as any)?.data?.data ?? (response as any)?.data ?? [];
        setReports(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error("Error fetching reports:", err);
        setReports([]);
        setListFetchError(
          err instanceof Error ? err.message : "Failed to load reports"
        );
      } finally {
        setIsFetching(false);
        setHasFetched(true);
      }
    };

    fetchReports();
  }, [isAllowed, isLoading, isAuthenticated, getAccessTokenSilently, tenantId]);

  // ------------------------------------------------------------------------------------
  // Filter logic
  // ------------------------------------------------------------------------------------
  function makeReportFields(): FilterField<Report>[] {

    return [
      {
        label: "ID",
        key: "id",
        type: "number",
        accessor: (r: Report) => r.id,
        inFreeText: true,
      },
      {
        label: "Customer Name",
        key: "customer_name",
        type: "string",
        accessor: (r: Report) => r.customer_name,
        inFreeText: true,
      },
      {
        label: "Customer ID",
        key: "customer_id",
        type: "string",
        accessor: (r: Report) => r.customer_id,
        inFreeText: true,
      },
      {
        label: "Date",
        key: "date",
        type: "string",
        accessor: (r: Report) => formatDateMonthYear(r.date),
        inFreeText: true,
      },
      {
        label: "Purchase Order",
        key: "reportGroupLabel",
        type: "string",
        accessor: (r: Report) => formatReportGroup(r),
        inFreeText: true,
      },
      {
        label: "Status",
        key: "status",
        type: "string",
        accessor: (r: Report) => r.status,
        inFreeText: true,
      },
    ];
  }

  const reportFields = useMemo(() => makeReportFields(), []);
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Report>>();
    for (const f of reportFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [reportFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of reportFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [reportFields]);

  const normalizedFilters = useMemo<ParsedFilter[]>(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":";
        }
      }
      
      // Date: always use "contains" so filtering works by year only, month only, or "Month, Year"
      if (token.key === "date" && token.value) {
        token.op = ":";
      }
            
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Report>(normalizedFilters, reportFields),
    [normalizedFilters, reportFields]
  );

  const filteredData = useMemo(
    () => reports.filter(predicate),
    [reports, predicate]
  );

  const suggestedKeys = useMemo(() => {
    return baseColumns.map((c) => c.header).filter(Boolean) as string[];
  }, [baseColumns]);

  const shouldFetch =
    isAllowed &&
    !isLoading &&
    isAuthenticated &&
    Boolean(tenantId);

  // ------------------------------------------------------------------------------------
  // Handle Download
  // ------------------------------------------------------------------------------------
  const handleDownload = async (report: Report) => {
    if (downloadingReportId === report.id) return; // Prevent duplicate downloads

    try {
      setDownloadingReportId(report.id);

      const token = await getAccessTokenSilently();
      const response = await downloadReports(token, report.id, tenantId);

      // Extract download URL and filename from response
      const downloadData = (response as any)?.data?.data;
      if (!downloadData || !downloadData.downloadUrl) {
        throw new Error("Download URL not found in response");
      }

      const { downloadUrl, filename } = downloadData;

      // Create a temporary link element to trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        filename || `Report_${report.reportMonth}_${report.reportYear}.pdf`;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(
        "Downloading report:",
        filename || `Report ${report.reportMonth}/${report.reportYear}`
      );
    } catch (error) {
      console.error("Error downloading report:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to download report. Please try again.";
      setErrorAlert({
        isOpen: true,
        message: errorMessage,
      });
    } finally {
      setDownloadingReportId(null);
    }
  };

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const columns = [
    ...baseColumns,
    {
      header: "Actions",
      accessor: "id" as keyof Report,
      renderCell: (row: Report) => {
        const isDownloading = downloadingReportId === row.id;
        const isCompleted = row.status === "completed";
        const isDisabled = isDownloading || !isCompleted;
        return (
          <div className="flex items-center gap-2">
            {/* Download Button */}
            <GenericButton
              variant="brand"
              label=""
              size="sm"
              isIconOnly={true}
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(row);
              }}
              icon={<IconDownload className="w-4 text-white fill-white" />}
              loading={isDownloading}
              disabled={isDisabled}
            />
          </div>
        );
      },
    },
  ];

  // ------------------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------------------
  return (
    <RequireTenantType allowedTypes={["PARTNER", "GLOBAL_ADMIN"]}>
      {shouldFetch && (!hasFetched || isFetching) ? (
        overlaySpinner
      ) : shouldFetch && listFetchError ? (
        <ErrorFetchingItems text={listFetchError} />
      ) : shouldFetch && reports.length === 0 ? (
        <EmptyItems />
      ) : (
        <div className="relative p-4 overflow-hidden flex flex-col h-full">
          <div className="relative flex-1 min-h-0">
            <GenericCard
              isCollapsible={false}
              className="h-full flex flex-col"
              bodyContent={
                <div className="flex flex-col h-full min-h-0">
                  <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                    <div className="flex items-center justify-between gap-4 my-5">
                      <div className="w-[520px]">
                        <GenericSearchbar
                          placeholder="Find by attribute or value"
                          suggestedKeys={suggestedKeys}
                          onFiltersChange={setActiveFilters}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 flex flex-col">
                    <GenericTable
                      columns={columns}
                      data={filteredData}
                      initialRowsPerPage={
                        filteredData.length > 0
                          ? Math.max(10, filteredData.length)
                          : 10
                      }
                      rowsPerPageOptions={
                        [
                          10,
                          25,
                          50,
                          100,
                          filteredData.length > 100
                            ? filteredData.length
                            : undefined,
                        ].filter(Boolean) as number[]
                      }
                      tableClassName="bg-[var(--semantic-bg-primary)]"
                      width="100%"
                      maxTableBodyHeight="100%"
                    />
                  </div>
                </div>
              }
            />
          </div>

          <GenericAlert
            isOpen={errorAlert.isOpen}
            onClose={() => setErrorAlert({ isOpen: false, message: "" })}
            onConfirm={() => setErrorAlert({ isOpen: false, message: "" })}
            variant="error"
            title="Download Error"
            description="There has been an error, please try again later"
            confirmLabel="OK"
            hideCancel={true}
          />
        </div>
      )}
    </RequireTenantType>
  );
}
